// mds-dojo — dijit/form/_TextBoxMixin — FAITHFUL port of
// references/dijit/form/_TextBoxMixin.js. Codemod pipeline; declare from the faithful
// runtime. Adaptations for this package:
//  - `dojo/sniff` → `dojo/has` (has("ie") is undefined under evergreen → the IE<=9
//    shims never run); unused `dojo/_base/array` import dropped.
//  - `dijit/main` is not a module here; the back-compat `dijit._setSelectionRange` /
//    `dijit.selectInputText` globals are attached to the published window.dijit
//    namespace (see registry.ts), with a local {} fallback.
//  - dojo-bidi dropped: class name loses "_NoBidi" (always "dijit.form._TextBoxMixin");
//    the trailing has("dojo-bidi") re-declare removed.
//  - `on.once` is not present on mds `dojo/on`, so a faithful local `once()` helper is
//    used; `on.emit` is accessed via cast (that call sits in a dead has("ie")<=9 block).
// A mixin for textbox form input widgets.
import declare from '../../dojo/_base/declare-runtime'; // declare
import dom from '../../dojo/dom'; // dom.byId
import has from '../../dojo/has';	// has("ie")
import keys from '../../dojo/keys'; // keys.ALT keys.CAPS_LOCK keys.CTRL keys.META keys.SHIFT
import lang from '../../dojo/_base/lang'; // lang.mixin
import on from '../../dojo/on'; // on

// dijit/main namespace (for the back-compat static exports below).
var dijit: any = (typeof window !== 'undefined' && (window as any).dijit) || {};

// Faithful substitute for on.once (fire the listener once, then self-remove).
function once(target: any, type: any, listener: any): any {
	var h: any = on(target, type, function (this: any) {
		h.remove();
		return listener.apply(this, arguments as any);
	});
	return h;
}

var _TextBoxMixin: any = declare("dijit.form._TextBoxMixin", null, {
	// summary:
	//		A mixin for textbox form input widgets

	// trim: Boolean
	//		Removes leading and trailing whitespace if true.  Default is false.
	trim: false,

	// uppercase: Boolean
	//		Converts all characters to uppercase if true.  Default is false.
	uppercase: false,

	// lowercase: Boolean
	//		Converts all characters to lowercase if true.  Default is false.
	lowercase: false,

	// propercase: Boolean
	//		Converts the first character of each word to uppercase if true.
	propercase: false,

	// maxLength: String
	//		HTML INPUT tag maxLength declaration.
	maxLength: "",

	// selectOnClick: [const] Boolean
	//		If true, all text will be selected when focused with mouse
	selectOnClick: false,

	// placeHolder: String
	//		Defines a hint to help users fill out the input field (as defined in HTML 5).
	//		This should only contain plain text (no html markup).
	placeHolder: "",

	_getValueAttr: function () {
		// summary:
		//		Hook so get('value') works as we like.
		// description:
		//		For `dijit/form/TextBox` this basically returns the value of the `<input>`.
		return this.parse(this.get('displayedValue'), this.constraints);
	},

	_setValueAttr: function (value: any, /*Boolean?*/ priorityChange?: any, /*String?*/ formattedValue?: any) {
		// summary:
		//		Hook so set('value', ...) works.
		var filteredValue;
		if (value !== undefined) {
			// TODO: this is calling filter() on both the display value and the actual value.
			filteredValue = this.filter(value);
			if (typeof formattedValue != "string") {
				if (filteredValue !== null && ((typeof filteredValue != "number") || !isNaN(filteredValue))) {
					formattedValue = this.filter(this.format(filteredValue, this.constraints));
				} else {
					formattedValue = '';
				}
				// Ensure the filtered value does not change after being formatted. See track #17955.
				if (this.compare(filteredValue, this.filter(this.parse(formattedValue, this.constraints))) != 0) {
					formattedValue = null;
				}
			}
		}
		if (formattedValue != null /* and !undefined */ && ((typeof formattedValue) != "number" || !isNaN(formattedValue)) && this.textbox.value != formattedValue) {
			this.textbox.value = formattedValue;
			this._set("displayedValue", this.get("displayedValue"));
		}

		this.inherited(arguments, [filteredValue, priorityChange]);
	},

	// displayedValue: String
	//		For subclasses like ComboBox where the displayed value
	//		(ex: Kentucky) and the serialized value (ex: KY) are different,
	//		this represents the displayed value.
	displayedValue: "",

	_getDisplayedValueAttr: function () {
		// summary:
		//		Hook so get('displayedValue') works.
		return this.filter(this.textbox.value);
	},

	_setDisplayedValueAttr: function (/*String*/ value: any) {
		// summary:
		//		Hook so set('displayedValue', ...) works.
		if (value == null /* or undefined */) {
			value = ''
		}
		else if (typeof value != "string") {
			value = String(value)
		}

		this.textbox.value = value;

		// sets the serialized value to something corresponding to specified displayedValue
		this._setValueAttr(this.get('value'), undefined);

		this._set("displayedValue", this.get('displayedValue'));
	},

	format: function (value: any /*=====, constraints =====*/) {
		// summary:
		//		Replaceable function to convert a value to a properly formatted string.
		return value == null /* or undefined */ ? "" : (value.toString ? value.toString() : value);
	},

	parse: function (value: any /*=====, constraints =====*/) {
		// summary:
		//		Replaceable function to convert a formatted string to a value
		return value;	// String
	},

	_refreshState: function () {
		// summary:
		//		After the user types some characters, etc., this method is
		//		called to check the field for validity etc.  The base method
		//		in `dijit/form/TextBox` does nothing, but subclasses override.
	},

	onInput: function (/*Event*/ /*===== evt =====*/) {
		// summary:
		//		Connect to this function to receive notifications of various user data-input events.
	},

	_onInput: function (/*Event*/ evt: any) {
		// summary:
		//		Called AFTER the input event has happened and this.textbox.value has new value.

		this._lastInputEventValue = this.textbox.value;

		// For Combobox, this needs to be called w/the keydown/keypress event that was passed to onInput().
		this._processInput(this._lastInputProducingEvent || evt);
		delete this._lastInputProducingEvent;

		if (this.intermediateChanges) {
			this._handleOnChange(this.get('value'), false);
		}
	},

	_processInput: function (/*Event*/ /*===== evt =====*/) {
		// summary:
		//		Default action handler for user input events.
		this._refreshState();

		// In case someone is watch()'ing for changes to displayedValue
		this._set("displayedValue", this.get("displayedValue"));
	},

	postCreate: function () {
		// setting the value here is needed since value="" in the template causes "undefined"
		// and setting in the DOM (instead of the JS object) helps with form reset actions
		this.textbox.setAttribute("value", this.textbox.value); // DOM and JS values should be the same

		this.inherited(arguments);

		// normalize input events to reduce spurious event processing
		function handleEvent(this: any, e: any) {
			var charOrCode;

			// Filter out keydown events that will be followed by keypress events.
			if (e.type == "keydown" && e.keyCode != 229) {
				charOrCode = e.keyCode;
				switch (charOrCode) { // ignore state keys
					case keys.SHIFT:
					case keys.ALT:
					case keys.CTRL:
					case keys.META:
					case keys.CAPS_LOCK:
					case keys.NUM_LOCK:
					case keys.SCROLL_LOCK:
						return;
				}
				if (!e.ctrlKey && !e.metaKey && !e.altKey) { // no modifiers
					switch (charOrCode) { // ignore location keys
						case keys.NUMPAD_0:
						case keys.NUMPAD_1:
						case keys.NUMPAD_2:
						case keys.NUMPAD_3:
						case keys.NUMPAD_4:
						case keys.NUMPAD_5:
						case keys.NUMPAD_6:
						case keys.NUMPAD_7:
						case keys.NUMPAD_8:
						case keys.NUMPAD_9:
						case keys.NUMPAD_MULTIPLY:
						case keys.NUMPAD_PLUS:
						case keys.NUMPAD_ENTER:
						case keys.NUMPAD_MINUS:
						case keys.NUMPAD_PERIOD:
						case keys.NUMPAD_DIVIDE:
							return;
					}
					if ((charOrCode >= 65 && charOrCode <= 90) || (charOrCode >= 48 && charOrCode <= 57) || charOrCode == keys.SPACE) {
						return; // keypress will handle simple non-modified printable keys
					}
					var named = false;
					for (var i in keys) {
						if (keys[i] === e.keyCode) {
							named = true;
							break;
						}
					}
					if (!named) {
						return;
					} // only allow named ones through
				}
			}

			charOrCode = e.charCode >= 32 ? String.fromCharCode(e.charCode) : e.charCode;
			if (!charOrCode) {
				charOrCode = (e.keyCode >= 65 && e.keyCode <= 90) || (e.keyCode >= 48 && e.keyCode <= 57) || e.keyCode == keys.SPACE ? String.fromCharCode(e.keyCode) : e.keyCode;
			}
			if (!charOrCode) {
				charOrCode = 229; // IME
			}
			if (e.type == "keypress") {
				if (typeof charOrCode != "string") {
					return;
				}
				if ((charOrCode >= 'a' && charOrCode <= 'z') || (charOrCode >= 'A' && charOrCode <= 'Z') || (charOrCode >= '0' && charOrCode <= '9') || (charOrCode === ' ')) {
					if (e.ctrlKey || e.metaKey || e.altKey) {
						return;
					} // can only be stopped reliably in keydown
				}
			}

			// create fake event to set charOrCode and to know if preventDefault() was called
			var faux: any = { faux: true }, attr;
			for (attr in e) {
				if (!/^(layer[XY]|returnValue|keyLocation)$/.test(attr)) { // prevent WebKit warnings
					var v = e[attr];
					if (typeof v != "function" && typeof v != "undefined") {
						faux[attr] = v;
					}
				}
			}
			lang.mixin(faux, {
				charOrCode: charOrCode,
				_wasConsumed: false,
				preventDefault: function () {
					faux._wasConsumed = true;
					e.preventDefault();
				},
				stopPropagation: function () {
					e.stopPropagation();
				}
			});

			this._lastInputProducingEvent = faux;

			// Give web page author a chance to consume the event.
			if (this.onInput(faux) === false) { // return false means stop
				faux.preventDefault();
				faux.stopPropagation();
			}
			if (faux._wasConsumed) {
				return;
			} // if preventDefault was called

			// IE8 doesn't emit the "input" event at all, and IE9 doesn't emit it for backspace, delete, cut, etc.
			if (has("ie") <= 9) {
				switch (e.keyCode) {
					case keys.TAB:
					case keys.ESCAPE:
					case keys.DOWN_ARROW:
					case keys.UP_ARROW:
					case keys.LEFT_ARROW:
					case keys.RIGHT_ARROW:
						break;
					default:
						if (e.keyCode == keys.ENTER && this.textbox.tagName.toLowerCase() != "textarea") {
							break;
						}
						this.defer(function (this: any) {
							if (this.textbox.value !== this._lastInputEventValue) {
								(on as any).emit(this.textbox, "input", { bubbles: true });
							}
						});
				}
			}
		}
		this.own(
			on(this.textbox, "keydown, keypress, paste, cut, compositionend", lang.hitch(this, handleEvent)),
			on(this.textbox, "input", lang.hitch(this, "_onInput")),

			// Allow keypress to bubble to this.domNode, so that TextBox.on("keypress", ...) works,
			// but prevent it from further propagating.
			on(this.domNode, "keypress", function (e: any) { e.stopPropagation(); })
		);
	},

	_blankValue: '', // if the textbox is blank, what value should be reported
	filter: function (val: any) {
		// summary:
		//		Auto-corrections (such as trimming) that are applied to textbox
		//		value on blur or form submit.
		if (val === null) {
			return this._blankValue;
		}
		if (typeof val != "string") {
			return val;
		}
		if (this.trim) {
			val = lang.trim(val);
		}
		if (this.uppercase) {
			val = val.toUpperCase();
		}
		if (this.lowercase) {
			val = val.toLowerCase();
		}
		if (this.propercase) {
			val = val.replace(/[^\s]+/g, function (word: string) {
				return word.substring(0, 1).toUpperCase() + word.substring(1);
			});
		}
		return val;
	},

	_setBlurValue: function () {
		// Format the displayed value, for example (for NumberTextBox) convert 1.4 to 1.400,
		// or (for CurrencyTextBox) 2.50 to $2.50
		this._setValueAttr(this.get('value'), true);
	},

	_onBlur: function (e: any) {
		if (this.disabled) {
			return;
		}
		this._setBlurValue();
		this.inherited(arguments);
	},

	_isTextSelected: function () {
		return this.textbox.selectionStart != this.textbox.selectionEnd;
	},

	_onFocus: function (/*String*/ by: string) {
		if (this.disabled || this.readOnly) {
			return;
		}

		// Select all text on focus via click if nothing already selected.
		if (this.selectOnClick && by == "mouse") {
			// Use once() to only select all text on first click only; otherwise users would have no way to clear
			// the selection.
			this._selectOnClickHandle = once(this.domNode, "mouseup, touchend", lang.hitch(this, function (this: any, evt: any) {
				// Check if the user selected some text manually (mouse-down, mouse-move, mouse-up)
				// and if not, then select all the text
				if (!this._isTextSelected()) {
					_TextBoxMixin.selectInputText(this.textbox);
				}
			}));
			this.own(this._selectOnClickHandle);

			// in case the mouseup never comes
			this.defer(function (this: any) {
				if (this._selectOnClickHandle) {
					this._selectOnClickHandle.remove();
					this._selectOnClickHandle = null;
				}
			}, 500); // if mouseup not received soon, then treat it as some gesture
		}
		// call this.inherited() before refreshState(), since this.inherited() will possibly scroll the viewport
		this.inherited(arguments);

		this._refreshState();
	},

	reset: function () {
		// Overrides `dijit/_FormWidget/reset()`.
		// Additionally resets the displayed textbox value to ''
		this.textbox.value = '';
		this.inherited(arguments);
	}
});

_TextBoxMixin._setSelectionRange = dijit._setSelectionRange = function (/*DomNode*/ element: any, /*Number?*/ start: any, /*Number?*/ stop: any) {
	if (element.setSelectionRange) {
		element.setSelectionRange(start, stop);
	}
};

_TextBoxMixin.selectInputText = dijit.selectInputText = function (/*DomNode*/ element: any, /*Number?*/ start?: any, /*Number?*/ stop?: any) {
	// summary:
	//		Select text in the input element argument, from start (default 0), to stop (default end).
	element = dom.byId(element);
	if (isNaN(start)) {
		start = 0;
	}
	if (isNaN(stop)) {
		stop = element.value ? element.value.length : 0;
	}
	try {
		element.focus();
		_TextBoxMixin._setSelectionRange(element, start, stop);
	} catch (e) { /* squelch random errors (esp. on IE) from unexpected focus changes or DOM nodes being hidden */
	}
};

export default _TextBoxMixin;
